import { Autocomplete, Container, FormHelperText, Grid, Typography } from "@mui/material";
import { styled } from "@mui/material/styles";
import { AsolviDatePicker } from "components/AsolviDatePicker";
import { AsolviSwitch } from "components/AsolviSwitch";
import { AsolviTimePicker } from "components/AsolviTimePicker";
import { InvoiceAutocomplete } from "components/job/InvoiceAutocomplete";
import PrimaryButton from "components/PrimaryButton";
import SecondaryButton from "components/SecondaryButton";
import StyledTextField from "components/StyledTextField";
import { DefinedDate, getInvoiceableDefaultValue, toDate, toDateString } from "helpers";
import technicianSettings from "helpers/technicianSettings";
import { Extra } from "models/extra";
import { ExtraValidationSchema } from "models/validation/ExtraValidationSchema";
import { ExtraInputType, ExtraType } from "operations/schema/schema";
import { FC, useCallback, useEffect, useMemo, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useAppSelector } from "store";
import { selectPageSelectedJob } from "store/root.store";
import { isFlagEnabled } from "store/slices/user.store";
import { ValidationError } from "yup";

const PREFIX = "AddExtra";

const classes = {
  flex: `${PREFIX}-flex`,
};

const StyledContainer = styled(Container)(({ theme }) => ({
  marginTop: theme.spacing(2),

  [`& .${classes.flex}`]: {
    marginTop: theme.spacing(2),
    display: "flex",
  },
}));

interface AddExtraProps {
  setOpenAddExtraDialog: (open: boolean) => void;
  loading: boolean;
  onAddExtra: (extra: ExtraInputType) => void;
  partnerView?: boolean;
}

export const AddExtraDialog: FC<AddExtraProps> = (props) => {
  const { loading, setOpenAddExtraDialog, onAddExtra, partnerView } = props;
  const intl = useIntl();
  const job = useAppSelector(selectPageSelectedJob);
  const { extras } = useAppSelector((state) => state.cache);
  const [errors, setErrors] = useState<any>({});
  const [selectedCode, setSelectedCode] = useState<String | null>(null);
  const [selectedExtra, setSelectedExtra] = useState<ExtraType | null>(null);
  const [isUseTime, setIsUseTime] = useState<boolean>(false);
  const [startTime, setStartTime] = useState<DefinedDate>(toDateString(new Date()));
  const [stopTime, setStopTime] = useState<DefinedDate>(toDateString(new Date()));
  const [quantity, setQuantity] = useState<string>("1");
  const regExpDecimal = new RegExp(`^-?\\d*.+\\d*$`);
  const [tempPrice, setTempPrice] = useState<string>("0");
  const [priceError, setPriceError] = useState<string>("");
  const [description, setDescription] = useState<string>("");
  const [salesPriceChangedOnClient, setSalesPriceChangedOnClient] = useState<boolean>(false);
  const [invoiceType, setInvoiceType] = useState<string | null>(
    getInvoiceableDefaultValue(job.contract?.typeObjNo),
  );

  const hasUpdatedServiceJobEndpoint = useAppSelector((s) =>
    isFlagEnabled(s, "UpdatedServiceJobEndpoint"),
  );
  const allowSalesPrice = technicianSettings.syncSalesPrice && hasUpdatedServiceJobEndpoint;
  const allowChangeInvoiceType =
    technicianSettings.allowChangeInvoiceType && hasUpdatedServiceJobEndpoint;

  const changePriceFromInput = useCallback(
    (value: string) => {
      if (!selectedExtra) return;
      setTempPrice(value);
      setSalesPriceChangedOnClient(true);
      const salesPrice = parseFloat(value);

      if (salesPrice > 0) {
        setPriceError("");
      } else {
        setPriceError(
          intl.formatMessage({
            id: "general.priceGreaterThanZero",
          }),
        );
      }
    },
    [intl, selectedExtra],
  );

  const changeInvoiceType = useCallback((value: string) => {
    setInvoiceType(value);
  }, []);

  useEffect(() => {
    if (job.machine?.servicesCode) {
      const extra = extras.find((x) => x.servicesCode === job.machine?.servicesCode);
      if (extra) setSelectedCode(extra.code);
    }
  }, [extras, job.machine?.servicesCode]);

  const sortedExtras = useMemo(() => {
    return extras
      .filter((e) => !!selectedCode && e.code === selectedCode)
      .sort((a, b) => {
        if (a.sortNo === null || a.sortNo === undefined) return 1;
        if (b.sortNo === null || b.sortNo === undefined) return -1;

        return a.sortNo - b.sortNo;
      });
  }, [extras, selectedCode]);

  return (
    <StyledContainer data-testid="AddPartDialog">
      <Grid container direction="column" spacing={2}>
        <Grid item>
          <Autocomplete
            fullWidth
            loading={loading}
            options={[...new Set(extras.map((e) => e.code))]}
            renderInput={(params) => (
              <StyledTextField {...params} label={intl.formatMessage({ id: "visit.extra.code" })} />
            )}
            value={selectedCode}
            data-testid="AddExtraDialog-Code"
            onChange={(_, value) => {
              setSelectedCode(value);
              setSelectedExtra(null);
            }}
            noOptionsText={intl.formatMessage({
              id: "general.noOptions",
            })}
            disabled={partnerView}
          />
        </Grid>
        <Grid item>
          <Autocomplete
            fullWidth
            loading={loading}
            options={sortedExtras}
            getOptionLabel={(e) => e.type}
            renderInput={(params) => (
              <StyledTextField {...params} label={intl.formatMessage({ id: "visit.extra.type" })} />
            )}
            value={selectedExtra}
            data-testid="AddExtraDialog-Type"
            onChange={(_, value) => {
              setSelectedExtra(value);
              setIsUseTime(value!.isTime);
            }}
            noOptionsText={intl.formatMessage({
              id: "general.noOptions",
            })}
          />
        </Grid>
        {!!selectedExtra && (
          <Grid
            item
            container
            alignItems="center"
            justifyContent="space-between"
            sx={{ pt: "10px", pb: "10px" }}
          >
            <Grid item sx={{ pl: "8px" }}>
              <Typography variant="body1">
                <FormattedMessage id="visit.extra.useTime" />
              </Typography>
            </Grid>
            <Grid item>
              <AsolviSwitch
                key="ExtrasSwitch"
                data-testid="AddExtras-Toggle"
                checked={isUseTime}
                onClick={() => {
                  setIsUseTime(!isUseTime);
                }}
              />
            </Grid>
          </Grid>
        )}
        {!!selectedExtra && isUseTime && (
          <>
            <Grid item container direction="row" spacing={1} wrap="nowrap">
              <Grid item xs={6}>
                <AsolviDatePicker
                  error={errors?.startDate}
                  name={`NewExtraStartDate`}
                  label={intl.formatMessage({ id: "times.startDate" })}
                  required
                  value={toDate(startTime)}
                  onChange={(value: Date) => setStartTime(toDateString(value))}
                />
              </Grid>
              <Grid item xs={6}>
                <AsolviTimePicker
                  error={errors?.startTime}
                  name={`NewExtraStartTime`}
                  label={intl.formatMessage({ id: "times.startTime" })}
                  required
                  value={toDate(startTime)}
                  onChange={(value: Date) => setStartTime(toDateString(value))}
                />
              </Grid>
            </Grid>
            <Grid item container direction="row" spacing={1} wrap="nowrap">
              <Grid item xs={6}>
                <AsolviDatePicker
                  error={errors?.stopDate}
                  name={`NewExtraStopDate`}
                  label={intl.formatMessage({ id: "times.endDate" })}
                  required
                  value={toDate(stopTime)}
                  onChange={(value: Date) => setStopTime(toDateString(value))}
                />
              </Grid>
              <Grid item xs={6}>
                <AsolviTimePicker
                  error={errors?.stopTime}
                  name={`NewExtraStartTime`}
                  label={intl.formatMessage({ id: "times.endTime" })}
                  required
                  value={toDate(stopTime)}
                  onChange={(value: Date) => setStopTime(toDateString(value))}
                />
              </Grid>
            </Grid>
          </>
        )}
        {!!selectedExtra && !isUseTime && (
          <>
            {hasUpdatedServiceJobEndpoint && (
              <Grid item container xs={12}>
                <StyledTextField
                  fullWidth
                  value={description}
                  id="description"
                  label={intl.formatMessage({ id: "general.description" })}
                  onChange={(e: any) => {
                    const { value } = e.target;
                    setDescription(value);
                  }}
                  multiline
                  rows={5}
                />
              </Grid>
            )}
            <Grid item>
              <Grid item xs={12}>
                <AsolviDatePicker
                  error={errors?.startDate}
                  name={`startDate`}
                  label={intl.formatMessage({ id: "times.startDate" })}
                  required
                  value={toDate(startTime)}
                  onChange={(value: Date) => setStartTime(toDateString(value))}
                />
              </Grid>
            </Grid>
            <Grid item xs={12}>
              <StyledTextField
                error={!!errors?.quantity}
                inputProps={{ step: "any" }}
                type="number"
                name="quantity"
                label={intl.formatMessage({ id: "general.quantity" })}
                value={quantity}
                onChange={(e: any) => {
                  const { value } = e.target;

                  if (regExpDecimal.test(value) || value === "") {
                    setQuantity(value);
                  }
                }}
              />
              <FormHelperText error>{errors?.quantity}</FormHelperText>
            </Grid>
            {allowSalesPrice && (
              <Grid item container xs={12}>
                <StyledTextField
                  value={tempPrice}
                  id="salesPrice"
                  type="number"
                  label={intl.formatMessage({ id: "general.salesPrice" })}
                  onChange={(event) => changePriceFromInput(event.target.value)}
                  helperText={priceError}
                  error={priceError !== ""}
                />
              </Grid>
            )}
          </>
        )}
        {allowChangeInvoiceType && (
          <Grid item container xs={12}>
            <InvoiceAutocomplete onChange={changeInvoiceType} value={invoiceType ?? null} />
          </Grid>
        )}
        <Grid item>
          <PrimaryButton
            key={`SaveExtraButton`}
            disabled={!selectedExtra}
            onClick={() => {
              const numQty = Number(quantity);
              const newExtra: Extra = {
                uploaded: false,
                pendingUpload: false,
                id: selectedExtra?.id || "",
                type: selectedExtra?.type || "",
                isTime: isUseTime,
                startTime: toDateString(startTime),
                stopTime: isUseTime ? toDateString(stopTime) : null,
                quantity: numQty !== 0 ? numQty : 1,
                servicesCode: selectedExtra?.servicesCode || "",
                salesPrice: parseFloat(tempPrice),
                salesPriceChangedOnClient,
                description,
                invoiceType,
              };

              const validationSchema = ExtraValidationSchema();
              const isValid = validationSchema.isValidSync(newExtra);
              if (isValid) {
                onAddExtra(newExtra);
                setErrors({});
                setSelectedExtra(null);
                setQuantity("1");
                setTempPrice("0");
                setDescription("");
                setInvoiceType(getInvoiceableDefaultValue(job.contract?.typeObjNo));
              } else {
                try {
                  validationSchema.validateSync(newExtra, { strict: true, abortEarly: false });
                } catch (e: any) {
                  let { inner } = e as ValidationError;
                  let errors: any = {};
                  for (let error of inner) {
                    if (!error.path) continue;
                    errors[error.path] = error.message;
                  }
                  setErrors({ ...errors });
                }
              }
            }}
          >
            <FormattedMessage id="visit.addExtra" />
          </PrimaryButton>
        </Grid>
        <Grid item>
          <SecondaryButton
            variant="contained"
            onClick={() => setOpenAddExtraDialog(false)}
            data-testid="AddExtraDialogFinishButton"
          >
            <FormattedMessage id="general.finish" />
          </SecondaryButton>
        </Grid>
      </Grid>
    </StyledContainer>
  );
};
